/**
 * The shelf cover of a favorite that is not a model (#2293): a document shows
 * its file-type icon, a functional its own icon (or the puzzle piece the
 * functionals catalog falls back to). Only models get a canvas preview.
 */
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPuzzlePiece } from '@fortawesome/pro-solid-svg-icons';
import ApiImage from '@/components/common/ApiImage';
import { iconForContentType } from '@/components/documents/documentFormat';
import { getExtValue } from '@/pages/learn/HelpFunctionalsPage/utils';
import type { DiscoverItem } from './useDiscoverData';

const TINTS = {
  document: ['#0f766e', '#0e7490', '#1e3a8a'],
  functional: ['#6d28d9', '#4338ca', '#0369a1'],
} as const;

export default function FavoriteCover({ item, kind, height = 130 }: Readonly<{
  item: DiscoverItem;
  kind: 'document' | 'functional';
  height?: number;
}>) {
  const [imgErr, setImgErr] = useState(false);
  const [c1, c2, c3] = TINTS[kind];
  const iconUrl = kind === 'functional' ? getExtValue(item.metadata?.ext, 'IconUrl') : undefined;
  const showImg = !!iconUrl && !imgErr;
  const icon = kind === 'document' ? iconForContentType(item.contentType) : faPuzzlePiece;

  return (
    <div
      data-cover={kind}
      style={{
        position: 'relative',
        height,
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${c1}, ${c2}, ${c3})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(120% 120% at 50% 40%, rgba(8,12,24,0.2), rgba(6,10,20,0.62))',
      }}/>
      {showImg ? (
        <ApiImage
          src={iconUrl}
          alt=""
          loading="lazy"
          onError={() => setImgErr(true)}
          style={{
            position: 'relative',
            maxWidth: '60%',
            maxHeight: '60%',
            objectFit: 'contain',
            filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.45))',
          }}
        />
      ) : (
        <FontAwesomeIcon
          icon={icon}
          style={{
            position: 'relative',
            fontSize: 44,
            color: 'rgba(255,255,255,0.9)',
            filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.4))',
          }}
        />
      )}
      <span style={{
        position: 'absolute',
        left: 10,
        bottom: 9,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'rgba(8,14,28,0.55)',
        border: '1px solid rgba(255,255,255,0.18)',
        color: 'rgba(255,255,255,0.88)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}>
        {kind === 'document' ? 'Document' : 'Functional'}
      </span>
    </div>
  );
}
